import { useState } from "react";
import { Table, Button, Modal, Form, Input, Select, DatePicker, Popconfirm, message, Space, Tag, Divider } from "antd";
import { Plus } from "lucide-react";
import dayjs from "dayjs";
import { useCOSHHSubstances, useCOSHHSubstanceCreate, useCOSHHSubstanceUpdate, useCOSHHSubstanceDelete, useCOSHHAssessments, useCOSHHAssessmentCreate, useCOSHHAssessmentUpdate, useCOSHHAssessmentDelete, useCCUsers } from "../../../../api-hooks/compliance";

const RISK_COLOR: Record<string, string> = { low: "green", medium: "orange", high: "red", very_high: "purple" };

export default function COSHHTab({ cid }: { cid: number }) {
  const [msgApi, ctx] = message.useMessage();
  const [form] = Form.useForm();
  const [subModal, setSubModal] = useState<{ open: boolean; rec: any }>({ open: false, rec: null });
  const [assModal, setAssModal] = useState<{ open: boolean; rec: any }>({ open: false, rec: null });

  const { data: substances = [], isLoading: sLoading } = useCOSHHSubstances(cid);
  const { data: assessments = [], isLoading: aLoading } = useCOSHHAssessments(cid);
  const { data: users = [] } = useCCUsers(cid);

  const cb = (msg: string, closeFn: () => void) => ({ onSuccess: () => { msgApi.success(msg); closeFn(); }, onError: (e: any) => msgApi.error(e?.message || "Error") });
  const sCreate = useCOSHHSubstanceCreate(cid, cb("Substance added.", () => setSubModal({ open: false, rec: null })));
  const sUpdate = useCOSHHSubstanceUpdate(cid, cb("Updated.", () => setSubModal({ open: false, rec: null })));
  const sDelete = useCOSHHSubstanceDelete(cid, { onSuccess: () => msgApi.success("Deleted."), onError: (e: any) => msgApi.error(e?.message) });
  const aCreate = useCOSHHAssessmentCreate(cid, cb("Assessment added.", () => setAssModal({ open: false, rec: null })));
  const aUpdate = useCOSHHAssessmentUpdate(cid, cb("Updated.", () => setAssModal({ open: false, rec: null })));
  const aDelete = useCOSHHAssessmentDelete(cid, { onSuccess: () => msgApi.success("Deleted."), onError: (e: any) => msgApi.error(e?.message) });

  const subCols = [
    { title: "Name", dataIndex: "name", key: "n" },
    { title: "CAS #", dataIndex: "cas_number", key: "cas", render: (v: string) => v || "—" },
    { title: "Hazard Class", dataIndex: "hazard_class", key: "hc", render: (v: string) => v || "—" },
    { title: "Active", dataIndex: "is_active", key: "a", render: (v: number) => <Tag color={v ? "green" : "default"}>{v ? "Yes" : "No"}</Tag> },
    { title: "", key: "act", render: (_: any, r: any) => (
      <Space size="small">
        <Button size="small" onClick={() => { setSubModal({ open: true, rec: r }); form.setFieldsValue(r); }}>Edit</Button>
        <Popconfirm title="Delete?" onConfirm={() => sDelete.mutate({ id: r.id })} okButtonProps={{ danger: true }}><Button size="small" danger>Del</Button></Popconfirm>
      </Space>
    )},
  ];

  const assCols = [
    { title: "Substance", dataIndex: "substance_name", key: "s" },
    { title: "Date", dataIndex: "assessment_date", key: "d", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—" },
    { title: "Risk Level", dataIndex: "risk_level", key: "r", render: (v: string) => <Tag color={RISK_COLOR[v]}>{v?.replace(/_/g, " ").toUpperCase()}</Tag> },
    { title: "Review Due", dataIndex: "review_date", key: "rd", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—" },
    { title: "Current", dataIndex: "is_current", key: "c", render: (v: number) => <Tag color={v ? "green" : "default"}>{v ? "Yes" : "No"}</Tag> },
    { title: "", key: "act", render: (_: any, r: any) => (
      <Space size="small">
        <Button size="small" onClick={() => { setAssModal({ open: true, rec: r }); form.setFieldsValue({ ...r, assessment_date: r.assessment_date ? dayjs(r.assessment_date) : null, review_date: r.review_date ? dayjs(r.review_date) : null }); }}>Edit</Button>
        <Popconfirm title="Delete?" onConfirm={() => aDelete.mutate({ id: r.id })} okButtonProps={{ danger: true }}><Button size="small" danger>Del</Button></Popconfirm>
      </Space>
    )},
  ];

  return (
    <div>
      {ctx}
      <div className="flex justify-between mb-2">
        <h3 className="font-semibold text-slate-700 text-sm">COSHH Substances</h3>
        <Button size="small" type="primary" icon={<Plus size={13} />} onClick={() => { setSubModal({ open: true, rec: null }); form.resetFields(); }} style={{ background: "#4c1d95" }}>Add Substance</Button>
      </div>
      <div className="bg-slate-50 rounded-lg border border-slate-100 mb-5">
        <Table rowKey="id" dataSource={substances as any[]} columns={subCols} loading={sLoading} size="small" pagination={false} />
      </div>
      <Divider plain className="my-3">COSHH Assessments</Divider>
      <div className="flex justify-end mb-2">
        <Button size="small" type="primary" icon={<Plus size={13} />} onClick={() => { setAssModal({ open: true, rec: null }); form.resetFields(); }} style={{ background: "#4c1d95" }}>Add Assessment</Button>
      </div>
      <Table rowKey="id" dataSource={assessments as any[]} columns={assCols} loading={aLoading} size="small" pagination={{ pageSize: 10 }} />

      {/* Substance Modal */}
      <Modal open={subModal.open} onCancel={() => setSubModal({ open: false, rec: null })} footer={null} destroyOnClose title={subModal.rec ? "Edit Substance" : "New COSHH Substance"} width={480}>
        <Form form={form} layout="vertical" size="middle" className="pt-2" onFinish={(vals) => {
          if (subModal.rec) sUpdate.mutate({ id: subModal.rec.id, ...vals }); else sCreate.mutate(vals);
        }}>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="name" label="Substance Name" rules={[{ required: true }]}><Input /></Form.Item>
            <Form.Item name="cas_number" label="CAS Number"><Input /></Form.Item>
          </div>
          <Form.Item name="hazard_class" label="Hazard Class"><Input /></Form.Item>
          <Form.Item name="sds_url" label="SDS / Safety Sheet URL"><Input /></Form.Item>
          <Form.Item name="storage_requirements" label="Storage Requirements"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="disposal_method" label="Disposal Method"><Input.TextArea rows={2} /></Form.Item>
          <div className="flex gap-2 pt-1">
            <Button type="primary" htmlType="submit" style={{ background: "#4c1d95" }} loading={sCreate.isPending || sUpdate.isPending}>Save</Button>
            <Button onClick={() => setSubModal({ open: false, rec: null })}>Cancel</Button>
          </div>
        </Form>
      </Modal>

      {/* Assessment Modal */}
      <Modal open={assModal.open} onCancel={() => setAssModal({ open: false, rec: null })} footer={null} destroyOnClose title={assModal.rec ? "Edit Assessment" : "New COSHH Assessment"} width={520}>
        <Form form={form} layout="vertical" size="middle" className="pt-2" onFinish={(vals) => {
          const p = { ...vals, assessment_date: vals.assessment_date?.format("YYYY-MM-DD"), review_date: vals.review_date?.format("YYYY-MM-DD") };
          if (assModal.rec) aUpdate.mutate({ id: assModal.rec.id, ...p }); else aCreate.mutate(p);
        }}>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="substance_id" label="Substance" rules={[{ required: true }]}><Select options={(substances as any[]).map(s => ({ value: s.id, label: s.name }))} /></Form.Item>
            <Form.Item name="risk_level" label="Risk Level" rules={[{ required: true }]}><Select options={["low","medium","high","very_high"].map(v => ({ value: v, label: v.replace(/_/g," ").toUpperCase() }))} /></Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="assessment_date" label="Assessment Date" rules={[{ required: true }]}><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
            <Form.Item name="review_date" label="Review Date"><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
          </div>
          <Form.Item name="assessed_by" label="Assessed By (User ID)"><Input type="number" /></Form.Item>
          <Form.Item name="control_measures" label="Control Measures"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="emergency_procedures" label="Emergency Procedures"><Input.TextArea rows={2} /></Form.Item>
          <div className="flex gap-2 pt-1">
            <Button type="primary" htmlType="submit" style={{ background: "#4c1d95" }} loading={aCreate.isPending || aUpdate.isPending}>Save</Button>
            <Button onClick={() => setAssModal({ open: false, rec: null })}>Cancel</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
